import { Anchor } from '@studio/design-system/button';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { openBridgeHelpArticle } from '#~/onboarding/help-actions.js';
import { STagNew } from '#~/stag/index.js';
import { Spinner } from '@studio/design-system/spinner';
import { useEffect, type ReactElement, type ReactNode } from 'react';
import type { AutoLinkState } from './auto-link-types.js';
import { noteBootProgress } from '#~/logger/forward.js';
import { DataSTag, Sel } from '#~/stag/index.js';

interface AutoLinkLoadingScreenProps {
  readonly state: AutoLinkState;
  readonly zeroStudioSetup?: boolean | undefined;
  readonly computerName?: string | undefined;
}

// True on the agent-download / setup onboarding routes — where the user has NO
// studio yet, so "Loading your studio…" is the wrong copy. Exported (pure) for
// unit testing. Covers /agent/setup and its children (select, download, link).
export const isOnboardingPath = (pathname: string): boolean => pathname.startsWith('/agent/setup');

// Exported for unit testing (pure: state + onboarding flag in, copy out).
export const getStatusMessage = (
  state: AutoLinkState,
  isOnboarding: boolean
): { title: string; subtitle: string } => {
  // Onboarding/no-studio copy. On /agent/setup the destination is the agent-
  // DOWNLOAD page — there is no studio to load — so the generic "Loading your
  // studio…" headline is wrong. The BootOverlay is normally dismissed here
  // (setup.tsx publishes isRootReady pre-paint), but it can still flash during
  // the brief pre-dismiss window OR a hard refresh landing on /agent/setup with
  // the registry load still in flight. Show neutral copy instead.
  // Per rule: reference-rules-in-comments (ai.knowledge/global).
  const ONBOARDING = { title: 'Getting things ready…', subtitle: 'Please wait' };

  switch (state.status) {
    case 'checking':
    case 'verifying_link':
      return isOnboarding ? ONBOARDING : { title: 'Loading your studio…', subtitle: 'Please wait' };
    case 'linking':
      return isOnboarding
        ? { title: 'Setting up your bridge…', subtitle: 'Please wait' }
        : { title: 'Loading your studio…', subtitle: 'Please wait' };
    case 'error':
      // ENG-4154 warm account-switch: stale_link_cleared self-heals via the
      // sidecar-driven reboot — cross-account copy here would only confuse
      // the user (engineer directive 2026-06-11). Render the benign linking
      // state; analytics still records the underlying error reason.
      if (state.reason === 'stale_link_cleared') {
        return { title: 'Setting up your bridge…', subtitle: 'Please wait' };
      }
      // bridge_unhealthy: the machine is already linked and the bridge did not
      // answer, so "continue to studio / link manually" would be wrong advice.
      // The controller message already carries the recovery step (restart).
      if (state.reason === 'bridge_unhealthy') {
        return { title: 'Bridge not responding', subtitle: 'Restart the app to reconnect.' };
      }
      return {
        title: 'Setup Issue Detected',
        subtitle: 'Continuing to studio. You can link your bridge manually if needed.'
      };
    default:
      return isOnboarding ? ONBOARDING : { title: 'Loading your studio…', subtitle: 'Please wait' };
  }
};

const SetupStep = ({
  active,
  complete,
  children
}: {
  readonly active: boolean;
  readonly complete: boolean;
  readonly children: ReactNode;
}): ReactElement => (
  <li className='flex items-center gap-8 text-[12px] text-muted'>
    <span
      aria-hidden='true'
      className={
        complete
          ? 'bg-success/20 flex size-[14px] items-center justify-center rounded-full text-[10px] text-success'
          : active
            ? 'border-white/20 size-[14px] animate-spin rounded-full border-2 border-t-focus'
            : 'size-[14px] rounded-full border border-white/10'
      }
    >
      {complete ? '✓' : ''}
    </span>
    {children}
  </li>
);

const ZeroStudioSetupScreen = ({
  state,
  computerName
}: {
  readonly state: AutoLinkState;
  readonly computerName: string;
}): ReactElement => {
  const phase =
    state.status === 'checking'
      ? 0
      : state.status === 'linking'
        ? 1
        : state.status === 'verifying_link'
          ? 2
          : 3;

  return (
    <div
      data-testid='loading-screen'
      {...DataSTag(Sel.BootOverlay, 'loading-screen')}
      className='flex h-screen w-full flex-col items-center justify-center bg-dark px-24'
    >
      <div className='animate-in fade-in flex w-full max-w-[380px] flex-col items-center duration-500'>
        <span
          data-testid='boot-spinner'
          {...DataSTag(Sel.BootOverlay, 'spinner')}
          className='mb-10'
        >
          <Spinner className='text-xl text-white' />
        </span>
        <p className='mb-10 text-[10px] font-demibold uppercase tracking-[0.08em] text-secondary'>
          First launch
        </p>
        <h1
          data-testid='loading-screen-text'
          {...DataSTag(Sel.BootOverlay, 'loading-text')}
          className='text-center text-[28px] font-demibold leading-[1.15] text-white'
        >
          Setting up your studio…
        </h1>
        <p className='mt-10 text-center text-[13px] leading-[1.5] text-muted'>
          {vendorName()} is creating a studio for {computerName} and linking this{' '}
          <Anchor
            role='link'
            aria-label={`bridge help for ${vendorName()}`}
            className='focus-visible:ring-primary/50 focus-visible:ring-2'
            {...STagNew(Sel.BootOverlay, 'bridge-help')}
            onClick={(event) => {
              event.preventDefault();
              openBridgeHelpArticle();
            }}
          >
            bridge
          </Anchor>{' '}
          to it.
        </p>
        <ol
          aria-live='polite'
          className='mt-24 flex w-full flex-col gap-10 rounded-8 bg-base-100 p-16 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]'
        >
          <SetupStep active={phase === 0} complete={phase > 0}>
            {phase > 0 ? 'Studio created' : 'Creating studio'}
          </SetupStep>
          <SetupStep active={phase === 1} complete={phase > 1}>
            Linking {computerName}
          </SetupStep>
          <SetupStep active={phase === 2} complete={phase > 2}>
            Verifying the bridge
          </SetupStep>
        </ol>
      </div>
    </div>
  );
};

export function AutoLinkLoadingScreen({
  state,
  zeroStudioSetup = false,
  computerName: machineName
}: AutoLinkLoadingScreenProps): ReactElement {
  // OS name lookups may return whitespace, not just undefined (notably on
  // Windows). Never render an empty subject in the first-launch checklist.
  const computerName = machineName?.trim() || 'this computer';
  // Read the path directly off window.location rather than useLocation(): this
  // component renders on routes both inside and outside a Router test harness
  // (boot-overlay, room, room-boot — see their tests), and the copy only needs a
  // point-in-time read, not reactive subscription. Avoids coupling the boot
  // surface to Router context. Per rule: reference-rules-in-comments.
  const { title, subtitle } = getStatusMessage(
    state,
    isOnboardingPath(typeof window === 'undefined' ? '' : window.location.pathname)
  );
  // stale_link_cleared is visually benign (see getStatusMessage): spinner
  // stays, no Error line — the reboot lands before the user should worry.
  const isError = state.status === 'error' && state.reason !== 'stale_link_cleared';
  // Spinner persists for every non-error state, including `success`. Excluding
  // `success` previously caused a visible spinner gap during SUCCESS_DELAY_MS.
  const isLoading = !isError;

  // Keep a log-level breadcrumb of substate. The visible text is intentionally
  // collapsed; production diagnosis of a stuck bridge probe relies on this.
  //
  // F2 (report §11.1): every auto-link substate transition is REAL forward
  // progress — the boot advanced a phase. Mark it so the TimeoutWatchdog leaves
  // an advancing-but-slow boot alone and only reboots one that has stopped
  // transitioning. This fires on transition (not on a timer), so a boot that
  // wedges in one phase stops marking progress and the watchdog can still
  // recover it. Per rule: reference-rules-in-comments (ai.knowledge/global).
  useEffect(() => {
    console.debug(`[auto-link-guard] state=${state.status}`);
    noteBootProgress();
  }, [state.status]);

  if (zeroStudioSetup) {
    return <ZeroStudioSetupScreen state={state} computerName={computerName} />;
  }

  return (
    // `data-testid="loading-screen"` marks the boot loading surface so the
    // BootOverlay / boot-sequence / studio-refresh e2e probes can count exactly
    // one loading screen across the boot lifecycle (Guard → Root → Placeholder)
    // and assert the refresh path mirrors cold-boot DOM structure. The probe
    // keys on this stable testid rather than CSS classes.
    <div
      data-testid='loading-screen'
      {...DataSTag(Sel.BootOverlay, 'loading-screen')}
      className='flex h-screen w-full flex-col items-center justify-center bg-dark'
    >
      <div className='animate-in fade-in flex flex-col items-center gap-6 duration-1000'>
        {/* Per gyfky-W1: `data-testid="boot-spinner"` wraps the spinner so the
         * BootOverlay regression test can assert spinner-presence across
         * the full boot lifecycle (Guard → Root → Placeholder). The inner
         * Spinner still emits its own testid; this wrapper is stable across
         * AutoLinkLoadingScreen call sites. */}
        {isLoading && (
          <span data-testid='boot-spinner' {...DataSTag(Sel.BootOverlay, 'spinner')}>
            <Spinner className='text-xl text-white' />
          </span>
        )}

        <div className='flex flex-col items-center text-center'>
          {/* `data-testid="loading-screen-text"` exposes the headline so the
           * boot-sequence probe can read the active loading copy (e.g. assert
           * the "Shutting down" variant during teardown). */}
          <h1
            data-testid='loading-screen-text'
            {...DataSTag(Sel.BootOverlay, 'loading-text')}
            className='font-semibold text-xl text-white'
          >
            {title}
          </h1>
          <p className='text-md text-muted'>{subtitle}</p>

          {isError && <p className='max-w-md mt-2 text-xs text-error'>Error: {state.message}</p>}
        </div>
      </div>
    </div>
  );
}
